
import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { regions, productLines, industries } from "./constants";

interface FilterHeaderProps {
  selectedRegion: string;
  selectedCountry: string;
  selectedAccountName: string;
  countryOptions: string[];
  accountOptions: string[];
  selectedProductLine: string;
  selectedIndustry: string;
  filterType: "product" | "industry";
  onRegionChange: (value: string) => void;
  onCountryChange: (value: string) => void;
  onAccountNameChange: (value: string) => void;
  onProductLineChange: (value: string) => void;
  onIndustryChange: (value: string) => void;
  onFilterTypeChange: (value: "product" | "industry") => void;
}

export const FilterHeader: React.FC<FilterHeaderProps> = ({
  selectedRegion,
  selectedCountry,
  selectedAccountName,
  countryOptions,
  accountOptions,
  selectedProductLine,
  selectedIndustry,
  filterType,
  onRegionChange,
  onCountryChange,
  onAccountNameChange,
  onProductLineChange,
  onIndustryChange,
  onFilterTypeChange
}) => {
  return (
    <div className="flex items-center justify-between mb-4">
      <h2 className="font-bold text-lg">Forecast Accuracy Analysis</h2>
      
      <div className="flex items-center space-x-3">
        <span className="text-primary-light font-medium">Filter by:</span>
                  
        <Select onValueChange={onRegionChange} value={selectedRegion}>
          <SelectTrigger className="w-auto min-w-[120px] border-primary/30 bg-secondary-light/5">
            <SelectValue placeholder="Select Region" />
          </SelectTrigger>
          <SelectContent>
            {regions.map(region => (
              <SelectItem key={region} value={region}>{region}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select onValueChange={onCountryChange} value={selectedCountry}>
          <SelectTrigger className="w-auto min-w-[120px] border-primary/30 bg-secondary-light/5">
            <SelectValue placeholder="Select Country" />
          </SelectTrigger>
          <SelectContent>
            {countryOptions.map(country => (
              <SelectItem key={country} value={country}>{country}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select onValueChange={onAccountNameChange} value={selectedAccountName}>
          <SelectTrigger className="w-auto min-w-[120px] border-primary/30 bg-secondary-light/5">
            <SelectValue placeholder="Select Account" />
          </SelectTrigger>
          <SelectContent>
            {accountOptions.map(account => (
              <SelectItem key={account} value={account}>{account}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <RadioGroup 
          className="flex flex-col items-start space-y-0 text-[10px]" 
          defaultValue="product" 
          value={filterType} 
          onValueChange={value => onFilterTypeChange(value as "product" | "industry")}
        >
          <div className="flex items-center space-x-1">
            <RadioGroupItem value="product" id="forecast-product" />
            <Label htmlFor="forecast-product" className="cursor-pointer text-white space-y-0 text-[10px]">Product</Label>
          </div>
          <div className="flex items-center space-x-1">
            <RadioGroupItem value="industry" id="forecast-industry" />
            <Label htmlFor="forecast-industry" className="cursor-pointer text-white space-y-0 text-[10px]">Industry</Label>
          </div>
        </RadioGroup>

        {filterType === "product" ? (
          <Select onValueChange={onProductLineChange} value={selectedProductLine}>
            <SelectTrigger className="w-auto min-w-[120px] border-primary/30 bg-secondary-light/5">
              <SelectValue placeholder="Select Product Line" />
            </SelectTrigger>
            <SelectContent>
              {productLines.map(productLine => (
                <SelectItem key={productLine} value={productLine}>{productLine}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Select onValueChange={onIndustryChange} value={selectedIndustry}>
            <SelectTrigger className="w-auto min-w-[120px] border-primary/30 bg-secondary-light/5">
              <SelectValue placeholder="Select Industry" />
            </SelectTrigger>
            <SelectContent>
              {industries.map(industry => (
                <SelectItem key={industry} value={industry}>{industry}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
};
